import _ from 'lodash';
import AnimationFrame from 'animation-frame';
import jQuery from 'jquery';
import LazyLoadingDOM from '../components/LazyLoadingDOM';
import LazyLoadingRepository from '../components/LazyLoadingRepository';

var options;

/**
 * Request Animation Frame ID
 *
 * @type    {Integer}
 */
var rafID = 0;

/**
 * RequestAnimationFrame instance
 *
 * @type    {Object}
 */
var raf = new AnimationFrame();

/**
 * State variables
 *
 * @type    {Object}
 */
var state = {
    container: null,
    height: 0,
    totalRows: 0,
    average: 0,
    delta: 0,
    breakpoints: [],
};

/**
 * Bootstraps the Lazy Loading component
 *
 * @param {Object} args Lazy Loading options
 *
 * @returns {void}
 */
var init = function (args) {

    options = args;
    setContainer();
    setTotalRows();
    setHeightAverage();
    initBreakpoints();
};

/**
 * Sets the initial breakpoints
 *
 * @returns {void}
 */
var initBreakpoints = function () {

    state.breakpoints[0] = state.average *
        (parseInt(options.totalItemsPerPage, 10) * 0.5);
    state.breakpoints[1] = state.average * 4 + state.breakpoints[0];
};

/**
 * Sets the container
 *
 * @returns {void}
 */
var setContainer = function () {

    state.container = document.querySelector(options.container);
    state.height = state.container.offsetHeight;
};

/**
 * Set the total number of rows present in the initial load
 *
 * @returns {void}
 */
var setTotalRows = function () {

    if (window.WH.sportsbook.marketsOnPage) {

        state.totalRows = parseInt(window.WH.sportsbook.marketsOnPage, 10);
    } else {

        state.totalRows = state.container.querySelectorAll(options.row).length;
    }
};

/**
 * Set the breakpoints
 *
 * @param   {Number}    current     current scroll position
 *
 * @returns {void}
 */
var setBreakpoints = function (current) {

    state.breakpoints[0] = current;
    state.breakpoints[1] = current + (4 * state.average);
};

/**
 * Calculates the height average of each row
 *
 * @returns {void}
 */
var setHeightAverage = function () {

    state.average = state.height / state.totalRows;
};

/**
 * Queues the next scrollUpdateCB
 *
 * @returns {void}
 */
var bind = function () {

    unbind();
    rafID = raf.request(updateCB);
};

/**
 * Cancels the next request animation frame call
 *
 * @returns {void}
 */
var unbind = function () {

    if (rafID) {

        raf.cancel(rafID);
        rafID = null;
    }
};

/**
 * Update callback for the request animation frame
 *
 * @returns {void}
 */
var updateCB = function () {

    if (state.totalRows === window.WH.sportsbook.totalRowsCount) {

        unbind();
        return;
    }

    updateCBImpl();
};

/**
 * Update callback for the request animation frame
 *
 * @returns {void}
 */
var updateCBImpl = function () {

    var currentScrollPos = window.pageYOffset;

    calculateDelta(currentScrollPos);
    loadMore();

    if (needsData(currentScrollPos)) {
        // request more data
        LazyLoadingRepository.request(state.totalRows);
    }

    bind();
};

/**
 * Returns true if data should be fetched from the server
 *
 * @param {Integer} currentScrollPos Current scroll position
 *
 * @returns {boolean}
 *
 * @private
 */
var needsData = function (currentScrollPos) {

    return currentScrollPos > state.breakpoints[0] || state.delta > 0;
};

/**
 * Calculates the delta movement
 *
 * @param currentScrollPos
 *
 * @returns {void}
 */
var calculateDelta = function (currentScrollPos) {

    // alternative delta value based on scroll position
    var deltaByOffset = getDeltaByOffset();

    if (!(currentScrollPos <= state.breakpoints[1] && deltaByOffset === 0)) {

        setDelta(currentScrollPos, deltaByOffset);
        setBreakpoints(currentScrollPos);
    }
};

/**
 * Function called after more rows were lazy loaded into the page.
 *
 * @param {Array} addedBatches DocumentFragments containing rows
 *
 * @returns {void}
 *
 * @private
 */
var postLoadMore = function (addedBatches) {

    if (_.isFunction(options.pagination.postLoad)) {

        options.pagination.postLoad(addedBatches);
    }
};

/**
 * Loads more rows into the page
 *
 * @returns {void}
 */
var loadMore = function () {

    if (!state.delta) {

        // check if we've passed the boundaries
        setTimeout(isOutOfBoundaries, 100);
        return;
    }

    var batches = LazyLoadingRepository.getTemplates(state.delta);

    var addedBatches = _.map(batches, addNode);

    postLoadMore(addedBatches);
};

/**
 * Checks if the breakpoints are outside the container boundaries and resets them if so
 *
 * @returns {void}
 *
 * @private
 */
var isOutOfBoundaries = function () {

    if (state && state.container && _.isArray(state.breakpoints)) {

        validateBoundaries(state.container.offsetHeight);
    }
};

/**
 * Validates if the current breakpoint is within the limits
 *
 * @param {Integer} containerHeight Container height
 *
 * @returns {void}
 *
 * @private
 */
var validateBoundaries = function (containerHeight) {

    var newBrkPoint = containerHeight - window.WH.sportsbook.eventsPerPage * state.average;

    if (containerHeight <= state.breakpoints[0]) {

        setBreakpoints(newBrkPoint);
    }
};

/**
 * Sets the delta movement
 *
 * @param   {number}    current     current scroll position
 * @param   {number}    deltaByOffset     Forced delta value based on scroll position
 *
 * @returns {void}
 */
var setDelta = function (current, deltaByOffset) {

    if (deltaByOffset) {

        state.delta = deltaByOffset;
    } else {

        // breakpoint cannot be bigger than current container height
        state.delta += Math.floor((current - state.breakpoints[0]) / state.average);
    }
};

/**
 * Determine if new markets should be loaded to DOM.
 * We aim to have buffer of elements exceeding viewport with height equal to screen height.
 *
 * If markets' container bottom position indicates there less then one screen buffer function will return true
 *
 * @returns {Boolean}
 */
var shouldUpdate = function shouldUpdate() {

    var endOfContainerPosition = jQuery(state.container).offset().top + state.container.offsetHeight;

    // Target position of container's bottom including minimum one screen of buffer
    var targetBufferEndPosition = window.pageYOffset + window.innerHeight * 2;

    return targetBufferEndPosition > endOfContainerPosition;
};

/**
 * Get delta value based on additional update condition - shouldUpdate
 *
 * @returns {Number}
 */
var getDeltaByOffset = function getDeltaByOffset() {

    if (shouldUpdate()) {

        // Load only small bunch on elements
        // if it's not enough next ones will be added on next animationFrame
        return 5;
    }

    return 0;
};

/**
 * Adds a batch node to the page
 *
 * @param {Object} batch The batch to add
 *
 * @return {DocumentFragment} The created document fragment
 */
var addNode = function (batch) {

    state.delta -= batch.totalItems;
    state.totalRows += batch.totalItems;

    return LazyLoadingDOM.addNode(batch);
};

/**
 * Destroys the lazy loading component
 *
 * @returns {void}
 */
var destroy = function () {

    unbind();

    options = {};
    state = {
        container: null,
        height: 0,
        totalRows: 0,
        average: 0,
        delta: 0,
        breakpoints: [],
    };
};

/**
 * Public Functions
 *
 * @type {Object}
 */
var api = {

    /**
     * Starts the Lazy loading view
     *
     * @param   {Object}    options     options to pass to the component
     *
     * @returns {void}
     *
     * @public
     */
    init: function (options) {

        // init internals
        init(options);

        // init our data repo
        LazyLoadingRepository.init(options, api.requestCB);

        // init the dom component
        LazyLoadingDOM.init(state.container, options);

        // bind to scroll
        bind();
    },

    /**
     * Destroys the lazy loading view
     *
     * @returns {void}
     *
     * @public
     */
    destroy: function () {

        if (options && options.pagination &&
            _.isFunction(options.pagination.abort)) {

            options.pagination.abort();
        }

        // reset state
        destroy();

        LazyLoadingRepository.destroy();
        LazyLoadingDOM.destroy();
    },

    /**
     * This is just a wrapper so we can send the current number of total rows in the page
     *
     * @param  {Object} data data received after performing the request to the server
     *
     * @return {void}
     *
     * @public
     */
    requestCB: function (data) {

        LazyLoadingRepository.requestCB(data, state.totalRows);
    },

    /**
     * Adds a note to a given position in the DOM
     *
     * @param {Object} node to be added
     * @param {number} position index for the node to be added
     *
     * @returns {boolean}
     *
     * @private
     */
    addNode: function (node, position) {

        // insert directly on the page
        if (position <= state.totalRows) {

            LazyLoadingDOM.addRowAtPosition(node, position);
            return true;
        }

        var item = {
            node: node,
            position: position,
        };

        // this may be rejected if out of range
        return LazyLoadingRepository.add(item, state.totalRows);
    },

    /**
     * Total rows
     *
     * @returns {Number}
     *
     * @public
     */
    getTotalRows: function () {

        return state.totalRows;
    },

    /**
     * Html string to element
     *
     * @param html
     *
     * @returns {DocumentFragment}
     *
     * @public
     */
    createElement: LazyLoadingDOM.createElement,
};

export default api;
